import type {
  MasterCompanyEmployeeBand,
  MasterCompanyIndustrySector,
} from "@legal-500/reference-data";
import { get, post, postWithSignal } from "./client";
import type { CompanyReviewEvent, ReviewPage } from "./pendingCompanies.service";

/**
 * A normalised CSV row as the import preview reports it. The preview accepts whatever the file
 * holds in the two enum-backed columns, so they stay free text here.
 */
export type MasterCompanyPreviewRow = {
  name: string;
  website: string | null;
  employees: string | null;
  industrySector: string | null;
};
/**
 * The create endpoint validates employees and industrySector against the reference-data enums,
 * so a request carrying anything else is rejected before it reaches the table.
 */
export type MasterCompanyCreateRequest = {
  name: string;
  website: string | null;
  employees: MasterCompanyEmployeeBand | null;
  industrySector: MasterCompanyIndustrySector | null;
};
export type WebsiteCompany = MasterCompanyPreviewRow & {
  revision: number;
  id: string;
  createdAt: string;
  updatedAt: string;
};
export type CompanyReviewPreview = WebsiteCompany & { affectedProfiles: number };
export const companyReviewPath = (id: string) => `/v1/website-companies/${id}/review`;
export type CompanySortField = "name" | "createdAt";
export type CompanySortDirection = "asc" | "desc";
export type ImportResult = {
  dryRun: boolean;
  rowsRead: number;
  inserted: number;
  skipped: number;
  preview: MasterCompanyPreviewRow[];
  warnings: string[];
};
export const masterCompaniesService = {
  audit: (id: string, limit: number, offset: number) =>
    get<ReviewPage<CompanyReviewEvent>>(
      `/v1/website-companies/${id}/audit?${new URLSearchParams({ limit: String(limit), offset: String(offset) })}`,
    ),
  review: (id: string) => get<{ data: CompanyReviewPreview }>(companyReviewPath(id)),
  list: (
    search: string,
    limit: number,
    offset: number,
    sort: CompanySortField,
    direction: CompanySortDirection,
  ) =>
    get<{ data: WebsiteCompany[]; meta: { total: number; limit: number; offset: number } }>(
      `/v1/website-companies?${new URLSearchParams({ search, sort, direction, limit: String(limit), offset: String(offset) })}`,
    ),
  // A bulk import is the longest call this UI makes, so the caller owns its signal and can stop
  // waiting on one that never answers.
  importCsv: (csv: string, dryRun: boolean, signal: AbortSignal) =>
    postWithSignal<ImportResult>("/v1/website-companies/import", { csv, dryRun }, signal),
  create: (company: MasterCompanyCreateRequest) =>
    post<{ inserted: number; skipped: number }>("/v1/website-companies", company),
};
